import { Box, Stack, styled, TextField } from '@mui/material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import Autocomplete, {
  AutocompleteProps,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  SELECT_CHEVRON_ABSOLUTE_SX,
  SelectChevronIcon,
} from '@/components/form/baseInputs/SelectChevronIcon';
import { UserPlus02Icon } from '@/components/icons/UserPlus02Icon';
import { MenuItemSecondaryTypography } from '@/components/typography/MenuItemSecondaryTypography';
import { MenuItemTypography } from '@/components/typography/MenuItemTypography';
import { COLORS } from '@/styles/tokens/colors';

import { FormControl } from '../FormControl';
import {
  BaseTypeaheadSelectInputProps,
  FormControlTypeaheadSelectInputProps,
  HelpTextVariant,
  SelectInputSize,
  TypeaheadSelectInputOption,
} from '../inputTypes';
import { SelectItemGroupLabel } from '../SelectInput/SelectItemGroupLabel';
import { TypeaheadClearButton } from '../TypeaheadClearButton';
import { TypeaheadOptionEndContent } from '../TypeaheadOptionEndContent';
import { useSelectHighlightedOptionOnTab } from '../useSelectHighlightedOptionOnTab';

export interface TypeaheadSelectProps<V>
  extends FormControlTypeaheadSelectInputProps<V> {
  label: string;
  id?: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  hideLabel?: boolean;
  autoSelect?: boolean;
  labelIconEnd?: React.ReactNode;
}

const GroupItems = styled('ul')({
  padding: 0,
});

const useStyles = makeStyles<{ size: SelectInputSize }>()(
  (_theme, { size }) => ({
    root: {
      '.MuiAutocomplete-inputRoot': {
        paddingTop: size === 'xs' ? '6px !important' : undefined,
        paddingRight: '32px !important',
        paddingBottom: size === 'xs' ? '6px !important' : undefined,
        paddingLeft: size === 'xs' ? '8px !important' : undefined,
      },
      '&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot': {
        paddingRight: '52px !important',
      },
      '.MuiAutocomplete-endAdornment': {
        alignItems: 'center',
        display: 'flex',
        right: '28px !important',
      },
      '.MuiAutocomplete-input': {
        padding: '0px !important',
      },
    },
  })
);

const ADD_NEW_OPTION = 'add_new_option';
const defaultFilterOptions =
  createFilterOptions<TypeaheadSelectInputOption<unknown>>();

const BaseTypeaheadSelectInput = ({
  options,
  testId,
  required,
  inputRef,
  startAdornment,
  dropdownWidth,
  dropdownProps = {},
  emptyOptionDisplay,
  hideEmptyOption: _hideEmptyOption,
  disableClearable = false,
  disablePortal = true,
  openOnFocus = true,
  addNewOption,
  size = 'sm',
  ...inputProps // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy types
}: BaseTypeaheadSelectInputProps<any>) => {
  const { classes } = useStyles({ size });
  const { disabled } = inputProps;
  // explicitly splitting off error and autocomplete so we don't pass them to
  // the top-level Autocomplete element, which doesn't accept them
  const {
    autoComplete,
    error,
    filterOptions = defaultFilterOptions,
    ...otherInputProps
  } = inputProps;
  const disabledStyle = disabled ? { background: COLORS.GRAY[50] } : {};
  const mergedSx = Object.assign({ background: 'white' }, disabledStyle);
  const placeholder = inputProps.placeholder ?? emptyOptionDisplay;
  const hasSelection =
    inputProps.value !== '' &&
    inputProps.value !== null &&
    inputProps.value !== undefined;

  const finalOptions = React.useMemo(() => {
    if (!addNewOption) return options;

    return [
      {
        display: addNewOption.text || 'Add new',
        value: ADD_NEW_OPTION,
        renderOption: (props: React.HTMLAttributes<HTMLLIElement>) => {
          // MUI includes the React key at runtime even though its shared
          // HTMLAttributes type omits it. Pass the key directly to React.
          const { key, ...optionProps } =
            props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

          return (
            <MenuItem
              key={key}
              {...optionProps}
              onClick={() => addNewOption.onClick()}
              sx={{
                height: 'auto',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
              }}
            >
              <ListItemIcon>
                {addNewOption.icon || <UserPlus02Icon size={16} />}
              </ListItemIcon>
              <ListItemText>{addNewOption.text || 'Add new'}</ListItemText>
            </MenuItem>
          );
        },
      },
      ...options,
    ];
  }, [addNewOption, options]);

  const finalFilterOptions = React.useCallback(
    (
      availableOptions: TypeaheadSelectInputOption<unknown>[],
      state: FilterOptionsState<TypeaheadSelectInputOption<unknown>>
    ) => {
      const addNew = availableOptions.find(
        (option) => option.value === ADD_NEW_OPTION
      );
      const filteredOptions = filterOptions(
        availableOptions.filter((option) => option.value !== ADD_NEW_OPTION),
        state
      );

      return addNew ? [addNew, ...filteredOptions] : filteredOptions;
    },
    [filterOptions]
  );

  const optionValueLabelMap = React.useMemo(() => {
    return finalOptions.reduce<Record<string, string>>((acc, option) => {
      acc[JSON.stringify(option.value)] = option.display;
      return acc;
    }, {});
  }, [finalOptions]);

  const handleChange: AutocompleteProps<
    TypeaheadSelectInputOption<unknown>,
    false,
    boolean,
    false
  >['onChange'] = (event, option, reason, details) => {
    if (option?.onSelectAction) {
      event.preventDefault();
      option.onSelectAction();
      return;
    }

    // Check if the selected option is the "add new" option
    if (addNewOption && option?.value === ADD_NEW_OPTION) {
      // Prevent default onChange behavior for the "add new" option
      event.preventDefault();

      // Trigger the add new option click handler
      addNewOption.onClick();

      return;
    }

    // Empty strings are the select input's established empty-value
    // contract, so normalize MUI's null clear value before forwarding it.
    const value = reason === 'clear' ? '' : option?.value;
    otherInputProps.onChange?.(event, value, reason, details);
  };

  const tabSelection = useSelectHighlightedOptionOnTab<
    TypeaheadSelectInputOption<unknown>
  >({
    selectOption: (event, option) =>
      handleChange(event, option, 'selectOption', { option }),
    onClose: otherInputProps.onClose,
  });

  return (
    // this select is a non-optimal experience for mobile; consider using NativeSelect here for a mobile device
    <Autocomplete<TypeaheadSelectInputOption<unknown>, false, boolean>
      autoHighlight
      disablePortal={disablePortal}
      forcePopupIcon={false}
      popupIcon={null}
      openOnFocus={openOnFocus}
      disableClearable={required || disableClearable || !hasSelection}
      className={classes.root}
      {...otherInputProps}
      onChange={handleChange}
      onHighlightChange={tabSelection.onHighlightChange}
      onKeyDown={tabSelection.onKeyDown}
      onClose={tabSelection.onClose}
      onInputChange={(_event, value, reason) =>
        tabSelection.trackInputChange(value, reason)
      }
      options={finalOptions}
      filterOptions={finalFilterOptions}
      isOptionEqualToValue={(option, value) => option?.value === value}
      // this method name is very misleading and poorly built. it actually passes the option when the
      // selection popover is opened, and passes just the value when computing the label when the
      // selection popover is closed. in github issues, they seem to feel strongly about keeping the `value`
      // as the exact value of one of the options.
      // see: https://github.com/mui/material-ui/issues/31192
      getOptionLabel={(valueOrOption) => {
        if (typeof valueOrOption === 'object' && 'display' in valueOrOption) {
          return valueOrOption.display;
        }

        if (valueOrOption === '' && placeholder) {
          return '';
        }

        return optionValueLabelMap[JSON.stringify(valueOrOption)] || '';
      }}
      slotProps={{
        clearIndicator: {
          component: TypeaheadClearButton,
          sx: {
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            borderRadius: '4px',
            height: '20px',
            margin: 0,
            padding: '1px',
            width: '20px',
            '&&:hover': {
              backgroundColor: COLORS.FUNCTIONAL.HOVER,
              borderColor: COLORS.FUNCTIONAL.HOVER,
            },
          },
        },
        popper: {
          placement: 'bottom-start',
          ...dropdownProps,
          style: {
            ...(dropdownWidth !== undefined && {
              width: dropdownWidth,
              maxWidth: 'calc(100vw - 32px)',
            }),
            ...dropdownProps.style,
          },
        },
      }}
      getOptionDisabled={(option) => !!option.disabled}
      renderGroup={(params) => (
        <li key={params.key}>
          {params.group && <SelectItemGroupLabel label={params.group} />}
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      renderOption={(props, option) => {
        if (option.renderOption) {
          return option.renderOption(props);
        }

        return (
          <Box
            component="li"
            {...props}
            key={`option-${String(option.value)}`}
            sx={{
              height: 'auto',
              overflowWrap: 'anywhere',
              py: option.secondaryText
                ? (theme) => theme.spacing(1.25)
                : undefined,
              // Match the icon gutter MUI applies inside a MenuItem so a
              // startAdornment lines up with SelectInput's option rows.
              '& .MuiListItemIcon-root': {
                minWidth: (theme) => theme.spacing(4.5),
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0}
                flex={1}
                minWidth={0}
              >
                {option.startAdornment}
                <Stack direction="column" spacing={0} minWidth={0}>
                  <MenuItemTypography>{option.display}</MenuItemTypography>
                  {option.secondaryText && (
                    <MenuItemSecondaryTypography>
                      {option.secondaryText}
                    </MenuItemSecondaryTypography>
                  )}
                </Stack>
              </Stack>
              <TypeaheadOptionEndContent
                endLabel={option.endLabel}
                badgeText={option.badgeText}
                badgeVariant={option.badgeVariant}
                endAdornment={option.endAdornment}
                endLabelLayout="stacked"
              />
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            variant="outlined"
            error={error}
            inputRef={inputRef}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <SelectChevronIcon
                    disabled={disabled}
                    sx={SELECT_CHEVRON_ABSOLUTE_SX}
                  />
                </>
              ),
              sx: mergedSx,
              inputProps: {
                ...params.inputProps,
                required,
                autoComplete,
                'aria-labelledby': otherInputProps['aria-labelledby'],
                'data-testid':
                  testId ?? `select-input-${inputProps.name ?? ''}`,
              },
            }}
          />
        );
      }}
    />
  );
};

export function TypeaheadSelectInput<V>({
  id,
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  labelIconEnd,
  ...inputProps
}: TypeaheadSelectProps<V>) {
  return (
    <FormControl<BaseTypeaheadSelectInputProps<V>>
      id={id}
      hideLabel={hideLabel}
      contextualHelp={contextualHelp}
      component={BaseTypeaheadSelectInput}
      label={label}
      labelIconEnd={labelIconEnd}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      required={inputProps.required}
      errorMessage={errorMessage}
      inputProps={inputProps}
      associateLabelWithAria={hideLabel}
    />
  );
}
